export interface TypeaheadDataEvent<T = unknown> {
  data: T[];
  id?: string;
  type?: string;
  /** Search scope of the picker this reply answers (#34282). */
  scope?: string;
}

/** A filterTypeahead request on the event bus. */
export interface TypeaheadFilterRequest {
  id: string;
  query?: string;
  initWithQuery?: string;
  /** Set by a picker modal so only the typeahead that opened it answers (#34282). */
  scope?: string;
}

export function createTypeaheadDataEvent<T>(
  id: string | undefined,
  data: T[],
  type?: string,
  scope?: string
): TypeaheadDataEvent<T> {
  return {
    id,
    type,
    data,
    ...(scope ? { scope } : {}),
  };
}

/**
 * `scope`, when the consumer has one, must match exactly: a picker opened by one
 * typeahead ignores results from any other typeahead with the same
 * formControlName, including unsolicited preloads that carry no scope.
 */
export function matchesTypeaheadDataEvent(
  payload: TypeaheadDataEvent<unknown> | null | undefined,
  id: string | undefined,
  type?: string,
  scope?: string
): boolean {
  if (!payload || !id || payload.id !== id) {
    return false;
  }

  if (type && payload.type && payload.type !== type) {
    return false;
  }

  if (scope && payload.scope !== scope) {
    return false;
  }

  return true;
}

/**
 * #34282: formControlName is not unique across live pages (the HSE Toolbox tabs
 * keep a 'facility' filter alive next to the Sign In Sheet 'facility' picker),
 * so a scoped request is answered only by the typeahead that owns the scope.
 * An unscoped request (page filters re-initialising a control) keeps matching
 * by formControlName.
 */
export function matchesTypeaheadFilterRequest(
  request: TypeaheadFilterRequest | null | undefined,
  id: string | undefined,
  ownScope: string
): boolean {
  if (!request || !id || request.id !== id) {
    return false;
  }
  return !request.scope || request.scope === ownScope;
}

/** A typeahead as seen by `isOwnFilterRequest`. */
export interface TypeaheadFilterOwner {
  config?: { formControlName?: string };
  searchScope?: string;
}

/** `matchesTypeaheadFilterRequest` for a typeahead instance. */
export function isOwnFilterRequest(
  request: TypeaheadFilterRequest | null | undefined,
  owner: TypeaheadFilterOwner
): boolean {
  return matchesTypeaheadFilterRequest(
    request,
    owner.config?.formControlName,
    owner.searchScope ?? ''
  );
}

let typeaheadSearchScopeSeq = 0;

/** A process-unique scope token for one typeahead instance. */
export function nextTypeaheadSearchScope(): string {
  typeaheadSearchScopeSeq += 1;
  return `typeahead-${typeaheadSearchScopeSeq}`;
}
